/**
 * Wire types + catálogos — Brief 5 A3 Branding (Sprint Frontend Brief 5 · Tela 4).
 *
 * Hand-written (decisão do projeto) espelhando o contrato REAL do Engine,
 * triangulado campo-a-campo (R15) contra:
 *   - apps/engine/src/modules/tenants/tenant-branding.service.ts (SerializedTenantBranding)
 *   - apps/engine/src/modules/tenants/dto/tenant-branding.schema.ts (UpdateTenantBrandingSchema)
 *   - packages/api-contracts/openapi/v1/{paths,components}/.../tenant-branding*.yaml
 * NÃO derivado do mockup cego (o protótipo expõe swatches/IDs próprios).
 *
 * Gate: `tenant-config-read` (GET) / `tenant-config-manage` (escrita). A escrita
 * exige tier >= Team no service (Solo recebe 403). `secondaryColor` + `secondaryFont`
 * são Team+ explícito. PATCH SEM optimistic-lock (não enviar version).
 */

// ─── Serializer REAL (GET /v1/tenant-branding → { data }) ─────────────────────

export interface BrandingCertification {
  id: string;
  label: string;
}

export interface SerializedTenantBranding {
  primaryColor: string | null;
  secondaryColor: string | null;
  /** enum REAL — ver SECONDARY_FONTS. */
  secondaryFont: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
  emailSignature: string | null;
  regulatedHeaderText: string | null;
  certifications: BrandingCertification[];
  version: number;
}

// ─── PATCH body (UpdateTenantBrandingSchema.strict()) ─────────────────────────
//
// Parcial: cada campo `undefined` é omitido do PATCH (não toca). `null` zera o
// campo no backend. Assets (logo/favicon) NÃO vão aqui — endpoints multipart.

export interface UpdateTenantBrandingRequest {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  secondaryFont?: SecondaryFont | null;
  emailSignature?: string | null;
  regulatedHeaderText?: string | null;
  certifications?: BrandingCertification[];
}

/**
 * Labels dos campos do PATCH de branding — mesmo texto dos títulos de card já
 * em uso na tela (não inventar strings novas). Usado pela `StickySaveBar`
 * (Admin Hub T7) para nomear o que mudou; chaves = `UpdateTenantBrandingRequest`.
 */
export const BRANDING_FIELD_LABELS: Record<keyof UpdateTenantBrandingRequest, string> = {
  primaryColor: 'Cor primária',
  secondaryColor: 'Cor secundária',
  secondaryFont: 'Fonte secundária',
  emailSignature: 'Assinatura de email',
  regulatedHeaderText: 'Texto do header',
  certifications: 'Logos de certificação',
};

// ─── Enums / limites do contrato ──────────────────────────────────────────────

/** enum REAL do DTO (`z.enum(SECONDARY_FONTS)`). */
export const SECONDARY_FONTS = ['Geist', 'Inter', 'Newsreader'] as const;
export type SecondaryFont = (typeof SECONDARY_FONTS)[number];

/** Campos que o backend exige tier >= Team (TEAM_PLUS_BRANDING_FIELDS). */
export const TEAM_PLUS_FIELDS = ['secondaryColor', 'secondaryFont'] as const;

/** hex #RRGGBB — mesmo regex do DTO (HEX_COLOR). */
export const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export const MAX_CERTIFICATIONS = 3;
export const MAX_TEXT_LEN = 2000;
export const MAX_ASSET_BYTES = 200 * 1024; // 200KB (alinhado ao service)

export type AssetKind = 'logo' | 'favicon';

/** MIME aceitos por kind (espelha ASSET_MIME_ALLOW do service). */
export const ASSET_ACCEPT: Record<AssetKind, { mimes: string[]; accept: string; hint: string }> = {
  logo: {
    mimes: ['image/png', 'image/svg+xml'],
    accept: 'image/png,image/svg+xml',
    hint: 'PNG ou SVG · até 200 KB',
  },
  favicon: {
    mimes: ['image/png', 'image/x-icon', 'image/vnd.microsoft.icon'],
    accept: 'image/png,image/x-icon,.ico',
    hint: '32×32 ou 48×48 px · PNG/ICO · até 200 KB',
  },
};

// ─── Catálogos de UI (design-system convention: swatches curados, NÃO picker livre) ──
//
// O mockup A3 define swatches curados (cada um mapeia para um hex que o backend
// aceita). Mantemos a convenção: o que persiste é o hex. Cores fora do catálogo
// (legado/custom) são renderizadas como "valor atual" sem swatch ativo.

export interface ColorSwatch {
  hex: string;
  name: string;
  hint: string;
}

export const PRIMARY_SWATCHES: readonly ColorSwatch[] = [
  { hex: '#D97706', name: 'Âmbar Lume', hint: 'padrão' },
  { hex: '#0066CC', name: 'Azul NetComet', hint: 'frio' },
  { hex: '#0F766E', name: 'Teal', hint: 'institucional' },
  { hex: '#7C3AED', name: 'Roxo', hint: 'criativo' },
];

export const SECONDARY_SWATCHES: readonly ColorSwatch[] = [
  { hex: '#78716C', name: 'Stone', hint: 'neutro quente' },
  { hex: '#64748B', name: 'Slate', hint: 'neutro frio' },
  { hex: '#65A30D', name: 'Sage', hint: 'accent verde' },
  { hex: '#E11D48', name: 'Rose', hint: 'accent destaque' },
];

export interface FontOption {
  id: SecondaryFont;
  family: string;
  hint: string;
}

export const FONT_OPTIONS: readonly FontOption[] = [
  { id: 'Geist', family: "'Geist', system-ui, sans-serif", hint: 'padrão Lume · sans neutro' },
  {
    id: 'Inter',
    family: "'Inter', system-ui, sans-serif",
    hint: 'alternativa · ampla legibilidade',
  },
  {
    id: 'Newsreader',
    family: "'Newsreader', Georgia, serif",
    hint: 'serif editorial · use em headlines',
  },
];

/**
 * Catálogo curado de certificações (Regulated). O backend persiste {id,label}
 * livremente; a UI oferece um conjunto canônico para evitar texto solto. O
 * `icon` é decorativo (client-side) — NÃO é persistido.
 */
export interface CertCatalogEntry {
  id: string;
  label: string;
  icon: string;
}

export const CERT_CATALOG: readonly CertCatalogEntry[] = [
  { id: 'pci-dss', label: 'PCI-DSS', icon: '🔒' },
  { id: 'lgpd', label: 'LGPD certified', icon: '🛡' },
  { id: 'hipaa', label: 'HIPAA', icon: '⚕' },
  { id: 'iso27001', label: 'ISO 27001', icon: '📜' },
  { id: 'anatel', label: 'Anatel', icon: '📡' },
  { id: 'soc2', label: 'SOC 2', icon: '✅' },
];

// ─── Tier helpers ─────────────────────────────────────────────────────────────

export type TenantTier = 'solo' | 'team' | 'professional' | 'regulated';

const TIER_RANK: Record<TenantTier, number> = {
  solo: 0,
  team: 1,
  professional: 2,
  regulated: 3,
};

/** tier >= Team → escrita liberada no service (Solo recebe 403). */
export function isTeamPlus(tier: string | null | undefined): boolean {
  if (tier == null) return false;
  const rank = TIER_RANK[tier as TenantTier];
  return rank !== undefined && rank >= TIER_RANK.team;
}

/** Seção de conformidade (headers/footers + certs) é conceito Regulated (mockup A3). */
export function isRegulated(tier: string | null | undefined): boolean {
  return tier === 'regulated';
}
